import { clsx } from 'clsx';
import { ShiftTypesEnum } from '../../../js/api';
import { StatusCodes } from '../constants/workflows';

export const APP_BG_COLOR = 'tw:bg-zinc-50';
export const STICKY_HEADER = clsx('tw:sticky', 'tw:top-0', 'tw:z-10', APP_BG_COLOR);

// COMMON DRAWER STYLES
export const READ_ONLY_LABEL_STYLE: string = 'tw:text-zinc-400 tw:!font-medium tw:text-xs';
export const READ_ONLY_VALUE_STYLE: string = 'tw:font-medium';
export const READ_ONLY_COL_MIN_WIDTH: string = 'tw:min-w-[120px]';

// SHIFT TYPE BUTTON STYLES
export const getShiftTypeButtonColor = (shiftType: ShiftTypesEnum, isSelected: boolean): string => {
  if (!isSelected) return 'tw:bg-zinc-100 tw:text-zinc-500 tw:border-zinc-200';
  switch (shiftType) {
    case ShiftTypesEnum.DAY:
      return 'tw:bg-amber-100 tw:text-amber-700 tw:border-amber-300';
    case ShiftTypesEnum.NIGHT:
      return 'tw:bg-blue-100 tw:text-blue-700 tw:border-blue-300';
    case ShiftTypesEnum._24H:
      return 'tw:bg-green-100 tw:text-green-700 tw:border-green-300';
    default:
      return 'tw:bg-zinc-200 tw:text-zinc-700 tw:border-zinc-300';
  }
};

// ASSIGNMENT STATUS STYLES
export const getAssignmentStatusClasses = (status: StatusCodes): string => {
  switch (status) {
    case StatusCodes.NEW:
    case StatusCodes.PROV_REV:
    case StatusCodes.PEND_FAC_REV:
    case StatusCodes.PEND_CONFIRM:
      return 'tw:bg-amber-100 tw:text-amber-900';
    case StatusCodes.CONFIRMED:
      return 'tw:bg-emerald-100 tw:text-emerald-900';
    case StatusCodes.COMPLETED:
      return 'tw:bg-zinc-100 tw:text-zinc-600';
    default:
      return '';
  }
};

// SHIFT STATUS BUCKETS — used by the Provider Home calendar to color a
// booked day by the shift's workflow status. Terminal-negative shift
// statuses (DECLINED, CANCELED, WITHDRAWN) bucket to null so callers can
// hide them entirely rather than paint them as booked.
export type ShiftStatusBucket = 'pending' | 'confirmed' | 'completed';

export const getShiftStatusBucket = (status: StatusCodes): ShiftStatusBucket | null => {
  switch (status) {
    case StatusCodes.PROPOSED:
    case StatusCodes.ACCEPTED:
      return 'pending';
    case StatusCodes.CONFIRMED:
      return 'confirmed';
    case StatusCodes.FINALIZED:
      return 'completed';
    default:
      return null;
  }
};

// INGEST FILE / ROW BADGE COLORS — staff ingestion pages. Maps workflow
// status / row outcome codes to Badge palette colors.
export type IngestBadgeColor = 'red' | 'amber' | 'green' | 'sky' | 'violet' | 'zinc';

export const getIngestFileStatusBadgeColor = (status: string): IngestBadgeColor => {
  switch (status) {
    case StatusCodes.PROCESSED:
      return 'green';
    case StatusCodes.PROC_WITH_ERRORS:
      return 'amber';
    case StatusCodes.FAILED:
      return 'red';
    case StatusCodes.PROCESSING:
    case StatusCodes.REPROCESSING:
      return 'sky';
    default:
      // PENDING, UPLOADED, NOT_LOADED
      return 'zinc';
  }
};

export const getIngestRowOutcomeBadgeColor = (outcome: string): IngestBadgeColor => {
  switch (outcome) {
    case 'created':
      return 'green';
    case 'updated':
      return 'sky';
    case 'duplicate':
      return 'amber';
    case 'skipped':
      // Valid but waiting (e.g. Time & Pay row awaiting the month's timecard).
      return 'violet';
    case 'error':
      return 'red';
    default:
      // pending, unchanged
      return 'zinc';
  }
};
